import type { DashboardsWidgetConfig } from '@smarthome/api';
import { ArrowLeft, Plus } from 'lucide-react';
import { useState, useCallback, useMemo } from 'react';

import { getAllWidgetDefinitions, getWidgetDefinition } from '../registry';
import type { WidgetDefinition, WidgetType } from '../types';

import { Button, SlideOutPanel, TextField } from '@/components/ui';

export interface AddWidgetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (type: WidgetType, title: string, config: DashboardsWidgetConfig) => void;
}

type Step = 'select' | 'configure';

export function AddWidgetModal({ isOpen, onClose, onAdd }: AddWidgetModalProps) {
  const [step, setStep] = useState<Step>('select');
  const [selectedType, setSelectedType] = useState<WidgetType | null>(null);
  const [title, setTitle] = useState('');
  const [config, setConfig] = useState<DashboardsWidgetConfig>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const widgets = useMemo(() => getAllWidgetDefinitions(), []);
  const selectedDef = selectedType ? getWidgetDefinition(selectedType) : null;

  const handleReset = useCallback(() => {
    setStep('select');
    setSelectedType(null);
    setTitle('');
    setConfig({});
    setErrors({});
  }, []);

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  const handleSelectType = useCallback((def: WidgetDefinition) => {
    setSelectedType(def.type);
    setTitle(def.displayName);
    setConfig(def.defaultConfig);
    setStep('configure');
  }, []);

  const handleBack = useCallback(() => {
    setStep('select');
    setSelectedType(null);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!selectedType || !selectedDef) return;

    // Validate config
    const result = selectedDef.configSchema.safeParse(config);
    if (!result.success) {
      const newErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const path = issue.path.join('.');
        newErrors[path] = issue.message;
      }
      setErrors(newErrors);
      return;
    }

    // Validate title
    if (!title.trim()) {
      setErrors({ title: 'Title is required' });
      return;
    }

    onAdd(selectedType, title.trim(), config);
    handleClose();
  }, [selectedType, selectedDef, config, title, onAdd, handleClose]);

  const ConfigForm = selectedDef?.ConfigForm;

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={handleClose}
      title={step === 'select' ? 'Add Widget' : `Configure ${selectedDef?.displayName}`}
      subtitle={
        step === 'select' ? 'Choose a widget type to add' : 'Set up your widget configuration'
      }
      size="lg"
      footer={
        step === 'configure' ? (
          <>
            <Button variant="ghost" onClick={handleBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button onClick={handleSubmit}>
              <Plus className="mr-2 h-4 w-4" />
              Add Widget
            </Button>
          </>
        ) : undefined
      }
    >
      {step === 'select' && (
        <div className="grid grid-cols-2 gap-3">
          {widgets.map((def) => {
            const Icon = def.icon;
            return (
              <button
                key={def.type}
                type="button"
                onClick={() => handleSelectType(def)}
                className="flex flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white p-4 text-center transition-all hover:border-blue-500 hover:bg-blue-50/50 hover:shadow-sm dark:border-white/[0.08] dark:bg-zinc-900 dark:hover:border-blue-500/50 dark:hover:bg-blue-500/10"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-zinc-800">
                  <Icon className="h-5 w-5 text-gray-600 dark:text-zinc-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {def.displayName}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-zinc-500">
                    {def.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {step === 'configure' && selectedDef && ConfigForm && (
        <div className="space-y-4">
          <TextField
            label="Widget Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={errors['title']}
            placeholder={selectedDef.displayName}
          />

          <div className="border-t border-gray-100 pt-4 dark:border-white/[0.04]">
            <ConfigForm
              config={config as typeof selectedDef.defaultConfig}
              onChange={setConfig}
              errors={errors}
            />
          </div>
        </div>
      )}
    </SlideOutPanel>
  );
}
